import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, MapPin, ArrowRight, Heart, Camera, Sparkles, Send, CheckCircle, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// CSS for confetti burst animation - plays once on page load
const confettiStyles = `
  @keyframes confetti-burst {
    0% {
      transform: translateY(-20px) rotate(0deg);
      opacity: 1;
    }
    100% {
      transform: translateY(100vh) rotate(720deg);
      opacity: 0;
    }
  }
  
  @keyframes confetti-shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-15px); }
    75% { transform: translateX(15px); }
  }
  
  .confetti-burst {
    position: absolute;
    animation: confetti-burst 4s ease-out forwards, confetti-shake 0.5s ease-in-out infinite;
  }
  
  .confetti-1 { left: 3%; top: -10px; animation-delay: 0s; background: #DE92A9; width: 12px; height: 12px; border-radius: 50%; }
  .confetti-2 { left: 8%; top: -20px; animation-delay: 0.1s; background: #FFD700; width: 10px; height: 10px; }
  .confetti-3 { left: 13%; top: -15px; animation-delay: 0.2s; background: #fff; width: 8px; height: 8px; border-radius: 50%; }
  .confetti-4 { left: 18%; top: -25px; animation-delay: 0.15s; background: #DE92A9; width: 14px; height: 14px; border-radius: 2px; }
  .confetti-5 { left: 23%; top: -10px; animation-delay: 0.25s; background: #FFD700; width: 10px; height: 10px; border-radius: 50%; }
  .confetti-6 { left: 28%; top: -30px; animation-delay: 0.05s; background: #fff; width: 12px; height: 12px; }
  .confetti-7 { left: 33%; top: -18px; animation-delay: 0.3s; background: #DE92A9; width: 8px; height: 8px; border-radius: 50%; }
  .confetti-8 { left: 38%; top: -22px; animation-delay: 0.12s; background: #FFD700; width: 14px; height: 14px; border-radius: 2px; }
  .confetti-9 { left: 43%; top: -12px; animation-delay: 0.22s; background: #fff; width: 10px; height: 10px; border-radius: 50%; }
  .confetti-10 { left: 48%; top: -28px; animation-delay: 0.08s; background: #DE92A9; width: 12px; height: 12px; }
  .confetti-11 { left: 53%; top: -16px; animation-delay: 0.18s; background: #FFD700; width: 8px; height: 8px; border-radius: 50%; }
  .confetti-12 { left: 58%; top: -24px; animation-delay: 0.28s; background: #fff; width: 14px; height: 14px; border-radius: 2px; }
  .confetti-13 { left: 63%; top: -14px; animation-delay: 0.03s; background: #DE92A9; width: 10px; height: 10px; border-radius: 50%; }
  .confetti-14 { left: 68%; top: -20px; animation-delay: 0.13s; background: #FFD700; width: 12px; height: 12px; }
  .confetti-15 { left: 73%; top: -26px; animation-delay: 0.23s; background: #fff; width: 8px; height: 8px; border-radius: 50%; }
  .confetti-16 { left: 78%; top: -18px; animation-delay: 0.07s; background: #DE92A9; width: 14px; height: 14px; border-radius: 2px; }
  .confetti-17 { left: 83%; top: -22px; animation-delay: 0.17s; background: #FFD700; width: 10px; height: 10px; border-radius: 50%; }
  .confetti-18 { left: 88%; top: -12px; animation-delay: 0.27s; background: #fff; width: 12px; height: 12px; }
  .confetti-19 { left: 93%; top: -28px; animation-delay: 0.1s; background: #DE92A9; width: 8px; height: 8px; border-radius: 50%; }
  .confetti-20 { left: 97%; top: -16px; animation-delay: 0.2s; background: #FFD700; width: 14px; height: 14px; border-radius: 2px; }
  
  /* Second wave */
  .confetti-21 { left: 5%; top: -35px; animation-delay: 0.35s; background: #fff; width: 10px; height: 10px; border-radius: 50%; }
  .confetti-22 { left: 15%; top: -40px; animation-delay: 0.4s; background: #DE92A9; width: 12px; height: 12px; }
  .confetti-23 { left: 25%; top: -32px; animation-delay: 0.45s; background: #FFD700; width: 8px; height: 8px; border-radius: 50%; }
  .confetti-24 { left: 35%; top: -38px; animation-delay: 0.5s; background: #fff; width: 14px; height: 14px; border-radius: 2px; }
  .confetti-25 { left: 45%; top: -42px; animation-delay: 0.55s; background: #DE92A9; width: 10px; height: 10px; border-radius: 50%; }
  .confetti-26 { left: 55%; top: -36px; animation-delay: 0.6s; background: #FFD700; width: 12px; height: 12px; }
  .confetti-27 { left: 65%; top: -44px; animation-delay: 0.65s; background: #fff; width: 8px; height: 8px; border-radius: 50%; }
  .confetti-28 { left: 75%; top: -34px; animation-delay: 0.7s; background: #DE92A9; width: 14px; height: 14px; border-radius: 2px; }
  .confetti-29 { left: 85%; top: -40px; animation-delay: 0.75s; background: #FFD700; width: 10px; height: 10px; border-radius: 50%; }
  .confetti-30 { left: 95%; top: -38px; animation-delay: 0.8s; background: #fff; width: 12px; height: 12px; }
`;

const RealWeddingsPage = () => {
  const [weddings, setWeddings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
    
    // Hide confetti after animation completes
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 5000);

    const fetchWeddings = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/weddings`);
        setWeddings(response.data);
      } catch (error) {
        console.error('Failed to load content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWeddings();
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      <SEO
        title="Real Weddings"
        description="Discover beautifully curated real weddings, heartfelt love stories, and the meaningful details that made each Maltese wedding day truly unforgettable."
      />
      {/* Inject confetti styles */}
      <style>{confettiStyles}</style>
      
      {/* Hero Section with Full-Width Image and Confetti Burst */}
      <section className="relative h-[85vh] min-h-[600px] overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1768611265618-86c1b1700188?w=1920&h=1080&fit=crop"
            alt="Wedding couple on beach at sunset"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/40"></div>
        </div>
        
        {/* Confetti Burst Animation - plays once on page load */}
        {showConfetti && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none z-20">
            <div className="confetti-burst confetti-1"></div>
            <div className="confetti-burst confetti-2"></div>
            <div className="confetti-burst confetti-3"></div>
            <div className="confetti-burst confetti-4"></div>
            <div className="confetti-burst confetti-5"></div>
            <div className="confetti-burst confetti-6"></div>
            <div className="confetti-burst confetti-7"></div>
            <div className="confetti-burst confetti-8"></div>
            <div className="confetti-burst confetti-9"></div>
            <div className="confetti-burst confetti-10"></div>
            <div className="confetti-burst confetti-11"></div>
            <div className="confetti-burst confetti-12"></div>
            <div className="confetti-burst confetti-13"></div>
            <div className="confetti-burst confetti-14"></div>
            <div className="confetti-burst confetti-15"></div>
            <div className="confetti-burst confetti-16"></div>
            <div className="confetti-burst confetti-17"></div>
            <div className="confetti-burst confetti-18"></div>
            <div className="confetti-burst confetti-19"></div>
            <div className="confetti-burst confetti-20"></div>
            <div className="confetti-burst confetti-21"></div>
            <div className="confetti-burst confetti-22"></div>
            <div className="confetti-burst confetti-23"></div>
            <div className="confetti-burst confetti-24"></div>
            <div className="confetti-burst confetti-25"></div>
            <div className="confetti-burst confetti-26"></div>
            <div className="confetti-burst confetti-27"></div>
            <div className="confetti-burst confetti-28"></div>
            <div className="confetti-burst confetti-29"></div>
            <div className="confetti-burst confetti-30"></div>
          </div>
        )}
        
        {/* Hero Content */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
          <p className="text-white/90 text-xs md:text-sm tracking-[0.4em] uppercase mb-6">
            Real love. Real celebrations.
          </p>
          <h1 
            className="text-5xl md:text-7xl lg:text-8xl text-white mb-8 tracking-tight"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            Real Weddings
          </h1>
          <div className="w-24 h-1 bg-[#DE92A9] mx-auto mb-8"></div>
          <p className="text-white/90 text-base md:text-lg leading-relaxed max-w-2xl">
            Discover beautifully curated real weddings, heartfelt love stories, and the meaningful details that made each day truly unforgettable.
          </p>
          
          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <ChevronDown size={32} className="text-white/70" />
          </div>
        </div>
      </section>

      {/* Get Featured Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-0 bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Image Side with Collage Effect */}
            <div className="relative h-[450px] md:h-auto bg-[#F5F3F0]">
              {/* Main Image */}
              <img 
                src="https://images.unsplash.com/photo-1519741497674-611481863552?w=600&h=700&fit=crop"
                alt="Wedding couple"
                className="w-full h-full object-cover"
              />
              {/* Heart Badge */}
              <div className="absolute top-6 left-6 bg-[#DE92A9] p-3 rounded-lg shadow-lg">
                <Heart size={24} className="text-white" fill="white" />
              </div>
              {/* Small Overlay Image */}
              <div className="absolute bottom-8 right-8 w-32 h-32 rounded-lg shadow-xl overflow-hidden border-4 border-white">
                <img 
                  src="https://images.unsplash.com/photo-1606800052052-a08af7148866?w=200&h=200&fit=crop"
                  alt="Wedding detail"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* "Your Photo Here" Badge */}
              <div className="absolute bottom-8 left-8 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-md">
                <p className="text-[#DE92A9] text-sm font-medium">Your photo here!</p>
              </div>
            </div>
            
            {/* Content Side */}
            <div className="p-8 md:p-12 flex flex-col justify-center">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-[#FDF2F4] text-[#DE92A9] px-4 py-2 rounded-full text-xs tracking-widest font-medium mb-6 w-fit">
                <Sparkles size={14} />
                GET FEATURED IN IDO MAGAZINE!
              </div>
              
              {/* Heading */}
              <h2 
                className="text-3xl md:text-4xl text-[#2C2C2C] mb-4 leading-tight"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                Just Said <span className="text-[#DE92A9]">"I Do"</span>?<br/>
                We Want to Celebrate You!
              </h2>
              
              {/* Description */}
              <p className="text-[#6B4E37] text-base mb-8">
                Your wedding day was magical, now let's share that magic with thousands of engaged couples across Malta!
              </p>
              
              {/* Feature List */}
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="bg-[#FDF2F4] p-2 rounded-lg flex-shrink-0">
                    <Camera size={20} className="text-[#DE92A9]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2C2C2C]">Share Your Beautiful Photos</h4>
                    <p className="text-sm text-[#6B4E37]">Send us your favorite shots, we love them all!</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-[#FDF2F4] p-2 rounded-lg flex-shrink-0">
                    <Heart size={20} className="text-[#DE92A9]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2C2C2C]">Tell Your Love Story</h4>
                    <p className="text-sm text-[#6B4E37]">How did you meet? We're all ears!</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-[#FDF2F4] p-2 rounded-lg flex-shrink-0">
                    <Sparkles size={20} className="text-[#DE92A9]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2C2C2C]">Inspire Future Brides & Grooms</h4>
                    <p className="text-sm text-[#6B4E37]">Your ideas could spark someone else's dream day!</p>
                  </div>
                </div>
              </div>
              
              {/* CTA Button */}
              <Link to="/submit-wedding">
                <Button className="w-full bg-[#DE92A9] hover:bg-[#C9758F] text-white py-6 text-sm tracking-wider rounded-lg group">
                  <Send size={18} className="mr-2" />
                  YES! SHARE MY WEDDING STORY
                  <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              
              {/* Note */}
              <p className="text-center text-sm text-[#8B7D6B] mt-4 flex items-center justify-center gap-2">
                <CheckCircle size={16} className="text-green-600" />
                It's FREE & takes only 5 minutes!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Stories Header */}
      <section className="pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-[#DE92A9] text-xs tracking-[0.3em] uppercase mb-4">
              Featured Stories
            </p>
            <h2 
              className="text-3xl md:text-4xl text-[#2C2C2C] mb-4"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              Recent Real Weddings
            </h2>
            <p className="text-[#6B4E37] max-w-2xl mx-auto">
              Explore beautiful weddings from couples who celebrated their love in Malta
            </p>
          </div>
        </div>
      </section>

      {/* Weddings Grid */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center text-[#8B7D6B]">Loading weddings...</div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {weddings.map((wedding) => (
                <Link 
                  key={wedding.id} 
                  to={`/wedding-stories/${wedding.slug || wedding.id}`}
                  data-testid={`wedding-card-${wedding.slug || wedding.id}`}
                >
                  <Card className="group overflow-hidden border-none shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer bg-white h-full">
                    {/* Image */}
                    <div className="relative h-72 overflow-hidden">
                      <img 
                        src={wedding.image} 
                        alt={wedding.couple}
                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      
                      {/* Read Story Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="bg-white/90 text-[#2C2C2C] px-4 py-2 rounded text-sm tracking-wider flex items-center gap-2">
                          READ STORY <ArrowRight size={14} />
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <CardContent className="p-6">
                      <h3 
                        className="text-2xl text-[#2C2C2C] mb-3 group-hover:text-[#DE92A9] transition-colors"
                        style={{ fontFamily: '"Playfair Display", serif' }}
                      >
                        {wedding.couple}
                      </h3>
                      
                      <div className="flex items-center gap-4 text-xs text-[#8B7D6B] mb-3">
                        <div className="flex items-center gap-1">
                          <Calendar size={14} />
                          <span>{wedding.date}</span>
                        </div>
                      </div>

                      <div className="flex items-start gap-1 text-xs text-[#8B7D6B] mb-4">
                        <MapPin size={14} className="mt-0.5 flex-shrink-0" />
                        <span className="line-clamp-1">{wedding.venue}</span>
                      </div>
                      
                      <p className="text-[#6B4E37] text-sm leading-relaxed line-clamp-3">
                        {wedding.description}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#DE92A9] via-[#E8AABC] to-[#DE92A9]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl md:text-4xl text-white mb-6"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            Your Story Deserves to be Told
          </h2>
          <p className="text-white/90 mb-8 text-lg">
            Fill out our simple submission form and your wedding could be featured in the next issue of I DO Magazine
          </p>
          <Link to="/submit-wedding">
            <Button className="bg-white text-[#DE92A9] hover:bg-[#FAF9F7] px-8 py-6 text-sm tracking-wider rounded-lg">
              FILL IN YOUR WEDDING STORY
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default RealWeddingsPage;
